var React = require('react');
var { useRef, useState, useEffect } = React;

function useNodeSelection(opts) {
  var canvasRef = opts.canvasRef;
  var canvasToolRef = opts.canvasToolRef;
  var spaceHeldRef = opts.spaceHeldRef;
  var panPosRef = opts.panPosRef;
  var zoomRef = opts.zoomRef;
  var workflowStateRef = opts.workflowStateRef;
  var getRect = opts.getRect;

  var [selectedNodeIds, setSelectedNodeIds] = useState({});
  var [lastSelectedNodeId, setLastSelectedNodeId] = useState(null);
  var [selectedConnectionId, setSelectedConnectionId] = useState(null);
  var [marquee, setMarquee] = useState(null);
  var marqueeRef = useRef(null);

  function onMarqueeMove(event) {
    if (!marqueeRef.current || !canvasRef.current) return;
    var m = marqueeRef.current;
    var canvasRect = canvasRef.current.getBoundingClientRect();
    var pan = panPosRef.current;
    var zoom = zoomRef.current;
    var worldX = (event.clientX - canvasRect.left - pan.x) / zoom;
    var worldY = (event.clientY - canvasRect.top - pan.y) / zoom;
    var rect = {
      x: Math.min(m.startWorldX, worldX),
      y: Math.min(m.startWorldY, worldY),
      width: Math.abs(worldX - m.startWorldX),
      height: Math.abs(worldY - m.startWorldY)
    };
    m.lastRect = rect;
    setMarquee(rect);
  }

  function stopMarquee(collapseAllExpandedGroups) {
    window.removeEventListener('mousemove', onMarqueeMove);
    window.removeEventListener('mouseup', _stopMarqueeHandler);
    if (!marqueeRef.current) { setMarquee(null); return; }

    var finalMarquee = marqueeRef.current.lastRect || null;
    setMarquee(null);

    if (finalMarquee && finalMarquee.width > 5 && finalMarquee.height > 5) {
      var currentNodes = workflowStateRef.current.nodes || [];
      var newSel = {};
      for (var i = 0; i < currentNodes.length; i++) {
        var n = currentNodes[i];
        if (!n.position) continue;
        var nRect = getRect(n);
        if (
          nRect.left + nRect.width > finalMarquee.x &&
          nRect.left < finalMarquee.x + finalMarquee.width &&
          nRect.top + nRect.height > finalMarquee.y &&
          nRect.top < finalMarquee.y + finalMarquee.height
        ) {
          newSel[n.id] = true;
        }
      }
      if (Object.keys(newSel).length > 0) {
        setSelectedNodeIds(newSel);
      }
    } else {
      setSelectedNodeIds({});
      if (collapseAllExpandedGroups) collapseAllExpandedGroups();
    }
    marqueeRef.current = null;
  }

  var _collapseCallbackRef = useRef(null);
  function _stopMarqueeHandler() {
    stopMarquee(_collapseCallbackRef.current);
  }

  function startMarquee(event, collapseAllExpandedGroups) {
    _collapseCallbackRef.current = collapseAllExpandedGroups;
    var canvasRect = canvasRef.current.getBoundingClientRect();
    var pan = panPosRef.current;
    var zoom = zoomRef.current;
    var worldX = (event.clientX - canvasRect.left - pan.x) / zoom;
    var worldY = (event.clientY - canvasRect.top - pan.y) / zoom;
    marqueeRef.current = {
      startWorldX: worldX,
      startWorldY: worldY,
      startClientX: event.clientX,
      startClientY: event.clientY
    };
    setMarquee({ x: worldX, y: worldY, width: 0, height: 0 });
    window.addEventListener('mousemove', onMarqueeMove);
    window.addEventListener('mouseup', _stopMarqueeHandler);
  }

  function clearConnectionSelection() {
    setSelectedConnectionId(null);
    setSelectedNodeIds({});
  }

  // Keyboard shortcuts
  useEffect(function() {
    function onKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'Escape') {
        setSelectedNodeIds({});
      }
      if (e.key === ' ' && !e.repeat && canvasToolRef.current === 'pointer') {
        e.preventDefault();
        spaceHeldRef.current = true;
        if (canvasRef.current) canvasRef.current.classList.add('workflow-canvas-space-pan');
      }
    }
    function onKeyUp(e) {
      if (e.key === ' ') {
        spaceHeldRef.current = false;
        if (canvasRef.current) canvasRef.current.classList.remove('workflow-canvas-space-pan');
      }
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return function() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return {
    selectedNodeIds: selectedNodeIds,
    setSelectedNodeIds: setSelectedNodeIds,
    lastSelectedNodeId: lastSelectedNodeId,
    setLastSelectedNodeId: setLastSelectedNodeId,
    selectedConnectionId: selectedConnectionId,
    setSelectedConnectionId: setSelectedConnectionId,
    marquee: marquee,
    startMarquee: startMarquee,
    clearConnectionSelection: clearConnectionSelection
  };
}

module.exports = useNodeSelection;
